import { VariantProps, cva } from "class-variance-authority";
import {
  ReactElement,
  isValidElement,
  cloneElement,
  PropsWithChildren,
} from "react";
import { createPath, NavLink, NavLinkProps } from "react-router";
import { cn } from "../utils/tailwind";

const navItemVariants = cva(
  "relative flex items-center gap-1 cursor-pointer p-2 text-base leading-6 whitespace-nowrap rounded-lg transition-colors",
  {
    variants: {
      variant: {
        primary: "hover:bg-grey-100",
        secondary: "hover:bg-grey-100",
        link: "hover:underline text-grey-700",
      },
      vertical: {
        true: "font-medium text-grey-800 mx-2",
        false: "font-normal text-grey-600",
      },
      state: {
        active: null,
        inactive: null,
      },
    },
    compoundVariants: [
      {
        variant: "secondary",
        vertical: false,
        state: "active",
        class: "text-brand-500 bg-grey-100",
      },
      {
        vertical: true,
        state: "active",
        class: " bg-grey-100",
      },
      {
        vertical: true,
        state: "inactive",
      },
      {
        vertical: false,
        variant: "primary",
        state: "active",
        class: "text-grey-900",
      },
      {
        vertical: false,
        variant: "primary",
        state: "inactive",
      },
    ],
    defaultVariants: {
      variant: "primary",
      vertical: false,
      state: "inactive",
    },
  },
);

type NavItemVariants = VariantProps<typeof navItemVariants>;

export type Variant = NavItemVariants["variant"];

interface Props
  extends
    Omit<NavItemVariants, "state">,
    Omit<NavLinkProps, "children" | "style">,
    PropsWithChildren {
  icon?: ReactElement<{ className?: string }>;

  /**
   * If true, the active indicator spans the full width under the text.
   * If false, it uses a fixed width of `w-5`.
   * @default true
   *
   * Note: This prop is ignored if `vertical` is set to true.
   */
  fullWidthIndicator?: boolean;

  isActive?: boolean;
}

export default function NavItem({
  children,
  icon,
  fullWidthIndicator = true,
  end = false,
  vertical = false,
  variant = "primary",
  isActive: forceActive,
  ...props
}: Props) {
  const body = (
    <>
      <span className="inline-flex sm:justify-start justify-between sm:w-auto w-full gap-1">
        {children}
        {isValidElement(icon) &&
          cloneElement(icon, {
            className: cn(icon.props.className, "text-grey-600 w-4 h-auto"),
          })}
      </span>
      <div
        className={cn(
          "active-indicator border-b border-brand-500 h-0.5 absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full",
          fullWidthIndicator && !vertical ? "w-full" : "w-5",
        )}
      />
    </>
  );

  if (props.target === "_blank") {
    const { to: href, ...linkProps } = props;
    return (
      <a
        {...linkProps}
        href={typeof href === "string" ? href : createPath(href)}
        target={props.target}
        rel="noopener noreferrer"
        className={cn(
          navItemVariants({
            variant,
            vertical,
            state: "inactive",
          }),
          "[&>.active-indicator]:hidden",
        )}
      >
        {body}
      </a>
    );
  } else {
    return (
      <NavLink
        end={end}
        className={({ isActive }) => {
          if (forceActive !== undefined) {
            isActive = forceActive;
          }
          const showIndicator = isActive && variant === "primary" && !vertical;

          return cn(
            navItemVariants({
              variant,
              vertical,
              state: isActive ? "active" : "inactive",
            }),
            !showIndicator && "[&>.active-indicator]:hidden",
          );
        }}
        {...props}
      >
        {body}
      </NavLink>
    );
  }
}
